import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";
import { investorSelector } from "@state/investor/investor.selectors";
import { logException } from "@helpers/logException/logException";
import { clientInvestSdk } from "@utils/clientInvestSdk";
import { captureScreenshot } from "./capture";
import { DEV_REPORT } from "./content";
import { DevReportModal } from "./DevReportModal";
import {
  describeBrowser,
  describeRoute,
  devReportEnvironment,
  resolveAppVersion,
  resolveBuildNumber,
  type DevReportEnvironment,
} from "./helpers";

const Launch = styled.button`
  display: inline-flex;
  align-items: center;
  height: ${() => 40}px;
  padding: 0 ${({ theme }) => theme.space.md}px;
  border: ${({ theme }) => theme.stroke.selected}px dashed
    ${({ theme }) => theme.colors.ui.border.error};
  border-radius: ${({ theme }) => theme.radius.full}px;
  background: ${({ theme }) => theme.colors.ui.background.error};
  color: ${({ theme }) => theme.colors.ui.text.error};
  box-shadow: ${({ theme }) => theme.elevation.medium};
  cursor: pointer;
  ${typographyCSS("label.sm")}
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0px;

  &:hover {
    border-style: solid;
  }

  &:disabled {
    cursor: progress;
  }
`;

interface DevReportFlowProps {
  environment: DevReportEnvironment;
}

const DevReportFlow = ({ environment }: DevReportFlowProps): JSX.Element => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  useEffect(
    () => () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    },
    [screenshotUrl]
  );

  const onLaunch = async () => {
    setIsCapturing(true);
    setError(null);
    setNote("");
    setIsSent(false);

    try {
      const captured = await captureScreenshot();
      setScreenshot(captured);
      setScreenshotUrl(URL.createObjectURL(captured));
    } catch (exception) {
      logException({
        exception,
        message: "Could not capture a dev report screenshot",
      });
      setScreenshot(null);
      setScreenshotUrl(null);
      setError(DEV_REPORT.captureFailed);
    }

    setIsCapturing(false);
    setIsOpen(true);
  };

  const onClose = useCallback(() => {
    setIsOpen(false);
    setScreenshot(null);
    setScreenshotUrl(null);
    setNote("");
    setError(null);
    setIsSent(false);
  }, []);

  const onSubmit = async () => {
    if (!screenshot) return;

    setIsSending(true);
    setError(null);

    try {
      await clientInvestSdk.createDevReport({
        screenshot,
        note: note.trim(),
        platform: "web",
        environment,
        appVersion: resolveAppVersion(),
        buildNumber: resolveBuildNumber(window.___webpackCompilationHash),
        routeName: describeRoute(window.location),
        ...describeBrowser({
          userAgent: window.navigator.userAgent,
          viewport: { width: window.innerWidth, height: window.innerHeight },
        }),
      });

      setIsSent(true);
    } catch (exception) {
      logException({ exception, message: "Could not send a dev report" });
      setError(DEV_REPORT.submitFailed);
    }

    setIsSending(false);
  };

  return (
    <>
      <Launch
        type="button"
        onClick={onLaunch}
        disabled={isCapturing}
        data-testid="dev-report-launch"
      >
        {isCapturing ? DEV_REPORT.capturing : DEV_REPORT.launch}
      </Launch>

      {isOpen ? (
        <DevReportModal
          screenshotUrl={screenshotUrl}
          note={note}
          error={error}
          isSending={isSending}
          isSent={isSent}
          onNoteChange={setNote}
          onSubmit={onSubmit}
          onClose={onClose}
        />
      ) : null}
    </>
  );
};

/**
 * Reports need a session for the API to attach an investor to, so the button
 * stays hidden until one exists.
 */
export const DevReportLauncher = (): JSX.Element | null => {
  const investor = useSelector(investorSelector);
  const environment = devReportEnvironment();

  if (!environment || !investor?.id) return null;

  return <DevReportFlow environment={environment} />;
};
